import { useCallback, useMemo, useState } from 'react';
import type { Question } from '@/types/theme.types';
import { firstPendingIndex, nextPendingIndex, tallySession } from '../initial-data.helpers';

/**
 * One question at a time over the sector's questions. Questions answered
 * before this session count as done and are not offered again.
 */
export function useQuestionSession(questions: readonly Question[], alreadySubmitted: ReadonlySet<string>) {
  const [submitted, setSubmitted] = useState<Set<string>>(() => new Set());
  const [skipped, setSkipped] = useState<Set<string>>(() => new Set());
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isComplete, setIsComplete] = useState(false);

  const done = useMemo(() => {
    const all = new Set(alreadySubmitted);
    submitted.forEach((id) => all.add(id));
    return all;
  }, [alreadySubmitted, submitted]);

  const start = useCallback(() => {
    setSubmitted(new Set());
    setSkipped(new Set());
    const first = firstPendingIndex(questions, alreadySubmitted);
    setCurrentIndex(Math.max(0, first));
    setIsComplete(first === -1);
  }, [questions, alreadySubmitted]);

  /**
   * Picks a paused session up again: the skipped questions stay skipped and the
   * question on screen comes back, unless it was answered since, in which case
   * the next pending one is shown.
   */
  const restore = useCallback(
    (currentQuestionId: string | null, skippedIds: readonly string[]) => {
      const known = new Set(questions.map((question) => question.id));
      const nextSkipped = new Set(skippedIds.filter((id) => known.has(id) && !alreadySubmitted.has(id)));
      setSubmitted(new Set());
      setSkipped(nextSkipped);
      const handled = new Set([...alreadySubmitted, ...nextSkipped]);
      const at = currentQuestionId ? questions.findIndex((question) => question.id === currentQuestionId) : -1;
      const index = at >= 0 && !handled.has(questions[at].id) ? at : nextPendingIndex(questions, at, handled);
      setCurrentIndex(Math.max(0, index));
      setIsComplete(index === -1);
    },
    [questions, alreadySubmitted],
  );

  const advance = useCallback(
    (handled: ReadonlySet<string>) => {
      const next = nextPendingIndex(questions, currentIndex, handled);
      if (next === -1) setIsComplete(true);
      else setCurrentIndex(next);
    },
    [questions, currentIndex],
  );

  const markSubmitted = useCallback(
    (questionId: string) => {
      const nextSubmitted = new Set(submitted).add(questionId);
      setSubmitted(nextSubmitted);
      const handled = new Set([...done, ...skipped, questionId]);
      advance(handled);
    },
    [submitted, done, skipped, advance],
  );

  const skip = useCallback(() => {
    const question = questions[currentIndex];
    if (!question) return;
    const nextSkipped = new Set(skipped).add(question.id);
    setSkipped(nextSkipped);
    advance(new Set([...done, ...nextSkipped]));
  }, [questions, currentIndex, skipped, done, advance]);

  const previous = useCallback(() => {
    setCurrentIndex((index) => Math.max(0, index - 1));
  }, []);

  /** Offers the skipped questions again, from the first one still pending. */
  const reviewSkipped = useCallback(() => {
    setSkipped(new Set());
    const first = firstPendingIndex(questions, done);
    if (first === -1) return;
    setCurrentIndex(first);
    setIsComplete(false);
  }, [questions, done]);

  const tally = useMemo(() => tallySession(questions, done, skipped), [questions, done, skipped]);

  return {
    current: questions[currentIndex] ?? null,
    currentIndex,
    isComplete,
    isCurrentDone: Boolean(questions[currentIndex] && done.has(questions[currentIndex].id)),
    done,
    skipped,
    submittedThisSession: submitted.size,
    tally,
    start,
    restore,
    markSubmitted,
    skip,
    previous,
    reviewSkipped,
  };
}
